import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { RichEditor, RichView } from "@/components/rich/RichEditor";

const cut = (c, sent) => {
  const i = c.indexOf(sent);
  if (i < 0) return c;
  const before = c.slice(0, i).replace(/[ \t]+$/, ""), after = c.slice(i + sent.length).replace(/^[ \t]+/, "");
  const join = before && after && !before.endsWith("\n") && !after.startsWith("\n") ? " " : "";
  return (before + join + after).replace(/\n{3,}/g, "\n\n");
};

const compose = (sections, dups, mode) => sections.filter((s) => s.content).map((s) => {
  let c = s.content;
  dups.filter((d) => d.section_id === s.id).forEach((d) => { c = mode === "strike" ? c.replace(d.sentence, `~~${d.sentence}~~`) : cut(c, d.sentence); });
  if (!c.trim()) return "";
  return s.is_target ? c : `## ${s.name}\n\n${c.trim()}`;
}).filter(Boolean).join("\n\n");

const DupRow = ({ d, kept, onToggle, disabled }) => (
  <li className="flex items-start gap-3 rounded-md border border-[#2D3442] bg-[#0F131A] p-2.5" data-testid={`merge-dup-${d.id}`}>
    <div className="min-w-0 flex-1 text-xs">
      <div className={kept ? "text-[#E8DFD1]" : "text-[#A8A090] line-through"}>{d.sentence}</div>
      <div className="mt-1 text-[#A8A090]">{d.fact ? `${d.fact}. ` : ""}{d.first_in ? `Already said in ${d.first_in}.` : "Already said earlier."}</div>
    </div>
    <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-[#A8A090]">Keep<Switch checked={kept} onCheckedChange={onToggle} disabled={disabled} data-testid={`merge-dup-keep-${d.id}`} /></label>
  </li>
);

export const MergePreview = ({ uid, targetId, ids, onFinal }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dedupe, setDedupe] = useState(true);
  const [kept, setKept] = useState(new Set());
  const [edited, setEdited] = useState(null);
  useEffect(() => {
    api.post(`/universes/${uid}/entities/merge/preview`, { target_id: targetId, source_ids: ids }).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const active = useMemo(() => (data && dedupe ? data.duplicates.filter((d) => !kept.has(d.id)) : []), [data, dedupe, kept]);
  const final = useMemo(() => (data ? compose(data.sections, active, "remove") : ""), [data, active]);
  const struck = useMemo(() => (data ? compose(data.sections, active, "strike") : ""), [data, active]);
  useEffect(() => { onFinal(edited ?? final, !data); }, [edited, final, data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <p className="text-sm text-[#F87171]" data-testid="merge-preview-error">{err}</p>;
  if (!data) return <p className="flex items-center gap-2 text-sm text-[#A8A090]" data-testid="merge-preview-loading"><Loader2 className="h-4 w-4 animate-spin" />Combining notes and checking for repeated facts…</p>;
  const toggle = (id) => setKept((k) => { const n = new Set(k); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="space-y-4" data-testid="merge-preview">
      {data.duplicates.length > 0 ? (
        <div className="space-y-2">
          <label className="flex items-center justify-between gap-3 text-sm text-[#F3EBDD]">
            <span>Remove repeated facts <span className="text-xs text-[#A8A090]">({data.duplicates.length} found)</span></span>
            <Switch checked={dedupe} onCheckedChange={setDedupe} disabled={edited != null} data-testid="merge-dedupe-switch" />
          </label>
          {dedupe && <ul className="max-h-48 space-y-1.5 overflow-y-auto pr-1">{data.duplicates.map((d) => <DupRow key={d.id} d={d} kept={kept.has(d.id)} onToggle={() => toggle(d.id)} disabled={edited != null} />)}</ul>}
        </div>
      ) : <p className="text-xs text-[#A8A090]" data-testid="merge-no-dups">No repeated facts found.</p>}
      <div className="flex items-center justify-between">
        <div className="lf-label">Combined note</div>
        {edited == null
          ? <Button size="sm" variant="ghost" onClick={() => setEdited(final)} data-testid="merge-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
          : <Button size="sm" variant="ghost" onClick={() => setEdited(null)} data-testid="merge-discard-edits"><RotateCcw className="mr-1 h-3.5 w-3.5" />Discard edits</Button>}
      </div>
      {edited != null && <p className="text-xs text-[#FBBF24]">You're editing the final note, so the repeat toggles are locked. Discard edits to change them.</p>}
      <div className="max-h-[45vh] overflow-y-auto rounded-md border border-[#2D3442] bg-[#0B0D10]">
        {edited == null
          ? <div className="px-4 py-3"><RichView markdown={struck || "*No notes to combine.*"} testid="merge-preview-view" /></div>
          : <RichEditor value={edited} onChange={setEdited} testid="merge-preview-editor" minH="min-h-[240px]" />}
      </div>
    </div>
  );
};
